import React, { useState, useEffect } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import { backendURL } from '../IPaddress';
import '../CSS/CRUDfiles.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import {
  CrudListShell,
  ExpandableRow,
  FormGrid,
  FormActions,
  ScheduleEmptyState,
  ConfirmDeleteModal,
  FilterBar,
  SearchInput,
  SortControl,
  ResultSummary,
  useListControls,
} from '../components/crud';

const USER_TYPE_SEARCH_FIELDS = ['type', 'description'];
const USER_TYPE_SORT_OPTIONS = [
  { key: 'type', label: 'Type', accessor: (t) => t.type?.toLowerCase() ?? '' },
];



const UserTypeComponent = () => {
  const [userTypes, setUserTypes] = useState([]);
  const [currentUserType, setCurrentUserType] = useState({ type: '', description: '' });
  const [showForm, setShowForm] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [userTypeToDelete, setUserTypeToDelete] = useState(null);
  const { authState } = useAuth();
  const navigate = useNavigate();

  const {
    items: visibleUserTypes,
    resultCount,
    totalCount,
    isFiltered,
    search,
    sort,
    clear,
  } = useListControls(userTypes, {
    searchFields: USER_TYPE_SEARCH_FIELDS,
    sortOptions: USER_TYPE_SORT_OPTIONS,
  });

  // Authentication check
  useEffect(() => {
    if (!authState) {
        navigate('/login');
    }
  }, [authState, navigate]);

  // Fetch all user types
  useEffect(() => {
    fetchUserTypes();
  }, []);

  const fetchUserTypes = async () => {
    const response = await fetch(`${backendURL}/api/usertypes`, {
      method: 'GET',
      credentials: 'include'
    });
    const data = await response.json();
    setUserTypes(data);
  };

  const handleFormChange = (e) => {
    setCurrentUserType({ ...currentUserType, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (currentUserType.type_id) {
      await updateUserType(currentUserType.type_id, currentUserType);
    } else {
      await createUserType(currentUserType);
    }
    setCurrentUserType({ type: '', description: '' });
    setShowForm(false);
    fetchUserTypes();
  };

  const createUserType = async (userTypeData) => {
    await fetch(`${backendURL}/api/usertypes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userTypeData),
      credentials: 'include'
    });
  };

  const updateUserType = async (typeId, userTypeData) => {
    await fetch(`${backendURL}/api/usertypes/${typeId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userTypeData),
      credentials: 'include'
    });
  };

  const deleteUserType = async () => {
    if (userTypeToDelete) {
      await fetch(`${backendURL}/api/usertypes/${userTypeToDelete}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      setUserTypeToDelete(null);
      setShowModal(false);
      fetchUserTypes();
    }
  };

  const editUserType = (userType) => {
    setCurrentUserType(userType);
    setShowForm(true);
  };

  const handleCancel = () => {
    setCurrentUserType({ type: '', description: '' });
    setShowForm(false);
  }

  const handleToggleForm = () => {
    if (!showForm) {
      setCurrentUserType({ type: '', description: '' });
    }
    setShowForm(!showForm);
  };

  const userTypeForm = (
    <Form onSubmit={handleSubmit}>
      <FormGrid>
        <Form.Group controlId="type">
          <Form.Label>User Type</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter user type"
            name="type"
            onChange={handleFormChange}
            value={currentUserType.type}
          />
        </Form.Group>
        <Form.Group controlId="description">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            placeholder="Enter description"
            name="description"
            onChange={handleFormChange}
            value={currentUserType.description}
          />
        </Form.Group>
        <FormActions>
          <Button type="submit">Submit</Button>
          <Button variant="secondary" onClick={handleCancel}>Cancel</Button>
        </FormActions>
      </FormGrid>
    </Form>
  );

  return (
    <div>
      <CrudListShell
        addLabel="Add New User Type"
        listLabel="Show User Types List"
        showForm={showForm}
        onToggleForm={handleToggleForm}
        tableHeading="User Types"
        form={userTypeForm}
        filters={(
          <FilterBar
            summary={(
              <ResultSummary
                count={resultCount}
                total={totalCount}
                noun="user type"
                onClear={clear}
                isFiltered={isFiltered}
              />
            )}
          >
            <SearchInput label="Search user types" placeholder="Type or description" {...search} />
            <SortControl {...sort} />
          </FilterBar>
        )}
        emptyState={(
          <tr>
            <td>
              <ScheduleEmptyState>No user types have been added yet.</ScheduleEmptyState>
            </td>
          </tr>
        )}
      >
        {visibleUserTypes.map((type) => (
          <ExpandableRow
            key={type.type_id}
            main={{ title: type.type, subtitle: type.description || 'No description provided.' }}
            actions={(
              <>
                <Button size="sm" onClick={() => editUserType(type)} aria-label="Edit user type">
                  <i className="bi bi-pencil"></i>
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    setUserTypeToDelete(type.type_id);
                    setShowModal(true);
                  }}
                  aria-label="Delete user type"
                >
                  <i className="bi bi-trash3"></i>
                </Button>
              </>
            )}
          />
        ))}
      </CrudListShell>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onConfirm={deleteUserType}
        bodyText="Are you sure you want to delete this user type?"
      />
    </div>
  );

};

export default UserTypeComponent;
